import { useTranslation } from 'react-i18next';
import { useMeasure } from 'react-use';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import { useState } from 'react';

import Modal, {
	ModalBackground,
	ModalContent,
} from 'packages/kakapo-ui-kit/components/modals/Modal';
import ModalHeader, {
	ModalHeaderTitle,
} from 'packages/kakapo-ui-kit/components/modals/ModalHeader';
import Button, { ButtonSize } from 'packages/kakapo-ui-kit/components/editors/Button';
import Toggle from 'packages/kakapo-ui-kit/components/forms/controls/Toggle';

import { ICertificateProps } from './designs/CertificateProps';
import CertificateOutlineDesign from './designs/CertificateOutlineDesign';

// Renders a div with 100% width relative to its parent container
// with the aspect ratio of a landscape A4 sheet
const ScaledContainer = styled.div`
	width: 100%;
	padding-top: 70.7%;
	position: relative;
`;

// Render millimeter-sized A4 in landscape, and scale to fit inside
// parent div
const A4 = styled.div<{ scale: number }>`
	position: absolute;
	top: 0;
	width: 297mm;
	height: 210mm;
	transform-origin: top left;
	transform: scale(${(props) => props.scale}, ${(props) => props.scale});
	overflow: hidden;
`;

export function CertificateA4({
	accentColor,
	attendeeName,
	eventDate,
	eventDescription,
	eventTitle,
	logo,
	signature,
	signerName,
	signerTitle,
	showBounds,
	scale,
}: { showBounds: boolean; scale: number } & ICertificateProps) {
	return (
		<A4 scale={scale}>
			<CertificateOutlineDesign
				accentColor={accentColor}
				showBounds={showBounds}
				attendeeName={attendeeName}
				eventDate={eventDate}
				eventDescription={eventDescription}
				eventTitle={eventTitle}
				logo={logo}
				signature={signature}
				signerName={signerName}
				signerTitle={signerTitle}
			/>
		</A4>
	);
}

function CertificatePreview({
	width,
	accentColor,
	attendeeName,
	eventDate,
	eventDescription,
	eventTitle,
	logo,
	signature,
	signerName,
	signerTitle,
	showLargePreview,
	onShowLargePreview,
}: {
	width?: number;
	showLargePreview: boolean;
	onShowLargePreview?: () => void;
} & ICertificateProps) {
	const { t } = useTranslation();
	const [containerRef, { width: containerWidth }] = useMeasure<HTMLDivElement>();
	const [showBounds, setShowBounds] = useState<boolean>(false);

	const dpi = 96;

	const widthInCentimeters = 29.7;
	const fullWidth = widthInCentimeters * (dpi / 2.54);

	const scale = containerWidth / fullWidth;

	return (
		<>
			<Box ref={containerRef} width={width ? `${width}mm` : '100%'}>
				<ScaledContainer>
					<CertificateA4
						scale={scale}
						accentColor={accentColor}
						showBounds={showBounds}
						attendeeName={attendeeName}
						eventDate={eventDate}
						eventDescription={eventDescription}
						eventTitle={eventTitle}
						logo={logo}
						signature={signature}
						signerName={signerName}
						signerTitle={signerTitle}
					/>
				</ScaledContainer>
			</Box>
			<Flex justifyContent="space-between" alignItems="center" mt={3}>
				<Box>
					<Toggle
						name="showBounds"
						value=""
						labelRight={t('common.settings.learningCertificateTemplates.edit.showBounds')}
						checked={showBounds}
						onChange={() => setShowBounds(!showBounds)}
						unspaced
					/>
				</Box>
				<Box>
					{showLargePreview && (
						<Button size={ButtonSize.Small} onClick={() => onShowLargePreview?.()}>
							{t('common.settings.learningCertificateTemplates.preview.button')}
						</Button>
					)}
				</Box>
			</Flex>
		</>
	);
}

export default function CertificatePreviewContainer({
	width,
	accentColor,
	attendeeName,
	eventDate,
	eventDescription,
	eventTitle,
	logo,
	signature,
	signerName,
	signerTitle,
}: { width?: number } & ICertificateProps) {
	const [showModal, setShowModal] = useState<boolean>(false);
	const { t } = useTranslation();

	return (
		<>
			<CertificatePreview
				accentColor={accentColor}
				attendeeName={attendeeName}
				eventDate={eventDate}
				eventDescription={eventDescription}
				eventTitle={eventTitle}
				logo={logo}
				signature={signature}
				signerName={signerName}
				signerTitle={signerTitle}
				showLargePreview
				onShowLargePreview={() => setShowModal(true)}
			/>
			{showModal && (
				<ModalBackground>
					<Modal width={1}>
						<ModalHeader onCancel={() => setShowModal(false)}>
							<ModalHeaderTitle>
								{t('common.settings.learningCertificateTemplates.preview.title')}
							</ModalHeaderTitle>
						</ModalHeader>
						<ModalContent>
							<CertificatePreview
								accentColor={accentColor}
								attendeeName={attendeeName}
								eventDate={eventDate}
								eventDescription={eventDescription}
								eventTitle={eventTitle}
								logo={logo}
								signature={signature}
								signerName={signerName}
								signerTitle={signerTitle}
								showLargePreview={false}
							/>
						</ModalContent>
					</Modal>
				</ModalBackground>
			)}
		</>
	);
}
